import React from 'react'
import styled from 'styled-components'
import { CheckIcon, UserIcon } from '@8x8/oxygen-icon'
import DropdownButton from '@8x8/oxygen-dropdown-button'
import List, { ListItem } from '@8x8/oxygen-list'
import { translate } from '~/core/translate'
import { LARGE_LAYOUT_THRESHOLD, ROLE } from '~/core/constants'
import { getInitials } from '~/core/utils'

const Container = styled.div``

const Body = styled.div`
  max-height: 285px;
  overflow: auto;
`

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: ${({ theme }) => `1px solid ${theme._neo.ui01}`};

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: ${LARGE_LAYOUT_THRESHOLD}px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`

const Col = styled.div`
  display: flex;
  align-items: center;
  color: inherit;
  flex:  ${({ align }) => align ? '1' : 'initial'};

  @media (max-width: ${LARGE_LAYOUT_THRESHOLD}px) {
    flex:  ${({ align }) => align ? '1 1 100%' : 'initial'};
  }
`

const Avatar = styled.div`
  background: ${({ theme }) => theme._neo.ui01};
  color: ${({ theme }) => theme._neo.textColor01};
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  font-size: 12px;
  font-weight: 700;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
`

const Name = styled.span`
  font-size: 15px;
`

const Role = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme._neo.textColor02};
`

const Item = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  gap: 16px;

  @media (max-width: ${LARGE_LAYOUT_THRESHOLD}px) {
    gap: 8px;
  }
`

const ItemIcon = styled.div`
  min-width: 24px;
  display: flex;
`

const Separator = styled.hr`
  border: none;
  height: 1px;
  background: ${({ theme }) => theme._neo.ui02};
`

const RemoveItem = styled(Item)`
  color: ${({ theme }) => theme._neo.action03};
`

const StyledDropdownButton = styled(DropdownButton)`
  color: ${({ theme }) => theme._neo.textColor01};
  background: ${({ theme }) => theme._neo.ui01};
  font-weight: 400;

  & > svg {
    fill: ${({ theme }) => theme._neo.textColor01};
  }
  
  &:hover {
    background: ${({ theme }) => theme._neo.ui02};
  }
`

const ParticipantsView = ({ defaultModerator, participants, onRoleUpdate, onRemove }) => (
  <Container>
    <Body>
      {participants?.map(({ name, userId, activeParticipantType }, index) => {
        const isModerator = activeParticipantType === ROLE.MODERATOR
        return (
          <Row key={`${userId}-${index}`}>
            <Col>
              <Avatar>{name ? getInitials(name) : <UserIcon />}</Avatar>
              <Name>{name || userId}</Name>
            </Col>
            <Col>
              {defaultModerator.id === userId
                ? (<Role>{translate('active_participants_header_moderator')}</Role>)
                : (
                  <StyledDropdownButton
                    buttonText={translate(activeParticipantType === ROLE.MODERATOR
                      ? 'active_participants_header_moderator'
                      : 'active_participants_header_participant')}
                    idealDirection='bottom'
                    aria-controls='roleoptionsmenu'
                    id='roledropdown'
                  >
                    <List role='menu' aria-labelledby='roledropdown' id='roleoptionsmenu'>
                      <ListItem
                        onClick={() => onRoleUpdate(userId, ROLE.MODERATOR)}
                        role='menuitem'
                      >
                        <Item>
                          <Col align>{translate('active_participants_header_moderator')}</Col>
                          <Col><ItemIcon>{isModerator && <CheckIcon />}</ItemIcon></Col>
                        </Item>
                      </ListItem>
                      <ListItem
                        onClick={() => onRoleUpdate(userId, ROLE.PARTICIPANT)}
                        role='menuitem'
                      >
                        <Item>
                          <Col align>{translate('active_participants_header_participant')}</Col>
                          <Col><ItemIcon>{!isModerator && <CheckIcon />}</ItemIcon></Col>
                        </Item>
                      </ListItem>
                      <Separator />
                      <ListItem
                        onClick={() => onRemove(userId)}
                        role='menuitem'
                      >
                        <RemoveItem>
                          <Col align>{translate('active_participants_header_remove')}</Col>
                        </RemoveItem>
                      </ListItem>
                    </List>
                  </StyledDropdownButton>
                  )}
            </Col>
          </Row>
        )
      })}
    </Body>
  </Container>
)

export default ParticipantsView
